import React, { useEffect, useState } from 'react';
import { Flow } from '../types/Document';
import { signImageUrlsInHtml } from '../lib/utils';

interface ResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  results: Flow[] | any;
  documentName: string;
}

const ResultsModal: React.FC<ResultsModalProps> = ({ isOpen, onClose, results, documentName }) => {
  const [signedResults, setSignedResults] = useState<Flow[] | any>(results);

  // Sign image URLs when results change
  useEffect(() => {
    const signImages = async () => {
      if (!results) return;
      
      try {
        // Deep clone the results to avoid mutating the original
        const cloned = JSON.parse(JSON.stringify(results));
        
        // Process all text fields that might contain images
        if (Array.isArray(cloned)) {
          for (const flow of cloned) {
            if (flow.phases) {
              for (const phase of flow.phases) {
                if (phase.steps) {
                  for (const step of phase.steps) {
                    if (step.blocks) {
                      for (const [blockType, block] of Object.entries(step.blocks)) {
                        if (block && typeof block === 'object' && 'text' in block) {
                          const blockObj = block as { text: string };
                          blockObj.text = await signImageUrlsInHtml(blockObj.text);
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        
        setSignedResults(cloned);
      } catch (error) {
        console.error('Error signing image URLs:', error);
        setSignedResults(results);
      }
    };
    
    signImages();
  }, [results]);

  if (!isOpen) return null;

  // Function to render HTML content safely
  const renderHtml = (html: string) => {
    return { __html: html };
  };

  // Add CSS styles for tables
  const tableStyles = `
    <style>
      table {
        border-collapse: collapse;
        width: 100%;
        margin-bottom: 1rem;
        border: 1px solid #e5e7eb;
      }
      th {
        background-color: #f3f4f6;
        font-weight: 600;
        text-align: left;
        padding: 0.75rem;
        border: 1px solid #e5e7eb;
      }
      td {
        padding: 0.75rem;
        border: 1px solid #e5e7eb;
      }
      tr:nth-child(even) {
        background-color: #f9fafb;
      }
    </style>
  `;

  // Function to render a table from markdown-like format
  const renderTable = (tableText: string) => {
    if (!tableText.includes('<table>')) return tableText;
    
    return tableStyles + tableText;
  };

  // Format the results for display
  const formatResults = (results: any) => {
    if (!results) return 'No results available';
    
    try {
      // If results is already a string, use it directly
      if (typeof results === 'string') {
        return results;
      }
      
      // Otherwise, pretty-print the JSON
      return JSON.stringify(results, null, 2);
    } catch (error) {
      console.error('Error formatting results:', error);
      return 'Error formatting results';
    }
  };

  // Determine if results contain sections that can be displayed separately
  const hasStructuredResults = signedResults && typeof signedResults === 'object' && !Array.isArray(signedResults);
  const isFlowArray = Array.isArray(signedResults) && signedResults.length > 0 && signedResults[0]?.Flow;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">
            Processing Results: {documentName}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(80vh-8rem)]">
          {isFlowArray ? (
            // Display nested list for array results (like the example provided)
            <div className="space-y-6">
              {(signedResults as Flow[]).map((flow, flowIndex) => (
                <div key={flowIndex} className="mb-6">
                  {/* Flow information */}
                  {flow.Flow && (
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-gray-800">{flow.Flow.name}</h3>
                      {flow.Flow.description && (
                        <p className="text-sm text-gray-600">{flow.Flow.description}</p>
                      )}
                    </div>
                  )}
                  
                  {/* Phases */}
                  {flow.phases && flow.phases.length > 0 && (
                    <div className="ml-4">
                      {flow.phases.map((phase, phaseIndex) => (
                        <div key={phaseIndex} className="mb-4">
                          <h4 className="text-md font-semibold text-gray-700 border-l-4 border-blue-500 pl-3 py-1 bg-blue-50">
                            {phase.name}
                          </h4>
                          
                          {/* Steps */}
                          {phase.steps && phase.steps.length > 0 && (
                            <div className="ml-6 mt-2">
                              {phase.steps.map((step, stepIndex) => (
                                <div key={stepIndex} className="mb-3 border-l-2 border-gray-200 pl-4">
                                  <h5 className="text-sm font-medium text-gray-700">{step.name}</h5>
                                  
                                  {/* Blocks */}
                                  {step.blocks && (
                                    <div className="ml-4 mt-2">
                                      {Object.entries(step.blocks).map(([blockType, block], blockIndex) => (
                                        <div key={blockIndex} className="mb-2">
                                          <h6 className="text-xs font-medium text-gray-600 capitalize">{blockType}</h6>
                                          <div className="text-sm text-gray-800 mt-1 bg-white p-3 rounded border border-gray-200">
                                            {block.text && (block.text.includes('<table>') || block.text.includes('<img')) ? (
                                              <div dangerouslySetInnerHTML={renderHtml(renderTable(block.text))} />
                                            ) : (
                                              <p className="whitespace-pre-wrap">{block.text}</p>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : hasStructuredResults ? (
            <div className="space-y-4">
              {Object.entries(signedResults).map(([key, value]) => (
                <div key={key} className="border rounded-md p-4 bg-white">
                  <h4 className="font-medium text-gray-700 mb-2 capitalize">{key.replace(/_/g, ' ')}</h4>
                  <pre className="bg-gray-50 p-3 rounded text-sm overflow-x-auto">
                    {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                  </pre>
                </div>
              ))}
            </div>
          ) : (
            <pre className="bg-gray-50 p-4 rounded text-sm overflow-x-auto">
              {formatResults(signedResults)}
            </pre>
          )}
        </div>
        
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResultsModal; 